import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiErrorDetail } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';

const EventSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings');
      setSettings(data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      toast.success(`${key} updated successfully`);
    } catch (error) {
      toast.error(`Failed to update ${key}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Update all settings
      for (const [key, value] of Object.entries(settings)) {
        await api.put(`/settings/${key}`, { value });
      }
      toast.success('All settings saved successfully');
    } catch (error) {
      toast.error('Failed to save some settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="text-white">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] pb-12">
      <div className="bg-[#121212] border-b border-[#D4AF37]/20 px-6 py-4 mb-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm" className="border-[#D4AF37]/30 text-[#D4AF37]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-white">Event Settings</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6">
            <h2 className="text-lg font-bold text-white mb-4">Event Information</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="event_title" className="text-white">Event Title</Label>
                <Input
                  id="event_title"
                  value={settings.event_title || ''}
                  onChange={(e) => updateSetting('event_title', e.target.value)}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                />
              </div>
              <div>
                <Label htmlFor="room_id" className="text-white">Room ID</Label>
                <Input
                  id="room_id"
                  value={settings.room_id || ''}
                  onChange={(e) => updateSetting('room_id', e.target.value)}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                />
              </div>
              <div>
                <Label htmlFor="hero_tagline" className="text-white">Hero Tagline</Label>
                <Input
                  id="hero_tagline"
                  value={settings.hero_tagline || ''}
                  onChange={(e) => updateSetting('hero_tagline', e.target.value)}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                />
              </div>
            </div>
          </Card>

          <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6">
            <h2 className="text-lg font-bold text-white mb-4">Dates & Times</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="campaign_start" className="text-white">Campaign Start Date</Label>
                  <Input
                    id="campaign_start"
                    type="date"
                    value={settings.campaign_start || ''}
                    onChange={(e) => updateSetting('campaign_start', e.target.value)}
                    className="bg-[#080808] border-[#D4AF37]/30 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="campaign_end" className="text-white">Campaign End Date</Label>
                  <Input
                    id="campaign_end"
                    type="date"
                    value={settings.campaign_end || ''}
                    onChange={(e) => updateSetting('campaign_end', e.target.value)}
                    className="bg-[#080808] border-[#D4AF37]/30 text-white"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="live_draw_date" className="text-white">Live Draw Date & Time</Label>
                <Input
                  id="live_draw_date"
                  type="datetime-local"
                  value={settings.live_draw_date ? settings.live_draw_date.slice(0, 16) : ''}
                  onChange={(e) => updateSetting('live_draw_date', e.target.value)}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                />
              </div>
            </div>
          </Card>

          <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6">
            <h2 className="text-lg font-bold text-white mb-4">Links</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="topup_url" className="text-white">Top-Up URL</Label>
                <Input
                  id="topup_url"
                  type="url"
                  value={settings.topup_url || ''}
                  onChange={(e) => updateSetting('topup_url', e.target.value)}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                  placeholder="https://example.com/topup"
                />
              </div>
              <div>
                <Label htmlFor="contact_url" className="text-white">Contact URL (WhatsApp)</Label>
                <Input
                  id="contact_url"
                  type="url"
                  value={settings.contact_url || ''}
                  onChange={(e) => updateSetting('contact_url', e.target.value)}
                  className="bg-[#080808] border-[#D4AF37]/30 text-white"
                  placeholder="https://wa.me/60123456789"
                />
              </div>
            </div>
          </Card>

          <Card className="bg-[#121212] border border-[#D4AF37]/10 p-6">
            <h2 className="text-lg font-bold text-white mb-4">Display Options</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="show_countdown"
                  checked={settings.show_countdown || false}
                  onCheckedChange={(checked) => updateSetting('show_countdown', checked)}
                />
                <Label htmlFor="show_countdown" className="text-white">Show Countdown Timer</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="mask_public_ids"
                  checked={settings.mask_public_ids !== undefined ? settings.mask_public_ids : true}
                  onCheckedChange={(checked) => updateSetting('mask_public_ids', checked)}
                />
                <Label htmlFor="mask_public_ids" className="text-white">Mask Sugo IDs on Public Pages</Label>
              </div>
            </div>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="px-8 py-4 rounded-full bg-[#D4AF37] text-[#080808] hover:bg-[#F3A6C8] hover:text-[#080808] transition-colors duration-200"
              data-testid="btn-save-settings"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? 'Saving...' : 'Save All Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventSettings;